import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Download, Search } from "lucide-react";
import Layout from "@/components/Layout";
import api, { formatApiError } from "@/lib/api";

// Phase 19.1 — auto-insert "/" slashes while the user types a DD/MM/YYYY
// value in a plain text input. Keeps only digits (up to 8) and formats as
// DD/MM/YYYY. Returning the pre-slash prefix during typing feels natural.
function _maskDdmmyyyy(raw) {
  const d = (raw || "").replace(/\D/g, "").slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
}

/**
 * Phase 14 — Generic tracker grid used by TransportTrackerPage and
 * GuestTrackerPage. Columns are described by `columns` prop; each column
 * has {key, label, type: "text"|"date"|"select", options?, width?}.
 *
 * Props:
 *   endpoint:    "/analytics/transport-tracker"
 *   exportPath:  "/analytics/transport-tracker/export.xlsx"
 *   heading:     "Transportation Bookings"
 *   columns:     [{ key, label, type, options?, width? }]
 *   dateRangeKey:"submitted_at" (for global date range filter label)
 */
export default function TrackerGrid({
  endpoint, exportPath, heading, columns, dateRangeKey = "submitted_at",
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [globalSearch, setGlobalSearch] = useState("");
  // Phase 19.1 — Date filters are DD/MM/YYYY text inputs. Browsers ignore the
  // placeholder on <input type="date"> and render a locale-dependent native
  // hint (often mm/dd/yyyy), which clashed with the rest of the app. A plain
  // text input honours the placeholder, so we accept DD/MM/YYYY from the
  // user and convert to ISO (YYYY-MM-DD) before sending to the API.
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [colFilters, setColFilters] = useState({});
  const [exporting, setExporting] = useState(false);

  // DD/MM/YYYY → YYYY-MM-DD, or "" if the input is partial/invalid.
  const toIso = (s) => {
    const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec((s || "").trim());
    if (!m) return "";
    const [, dd, mm, yyyy] = m;
    const d = Number(dd), mo = Number(mm);
    if (mo < 1 || mo > 12 || d < 1 || d > 31) return "";
    return `${yyyy}-${mm}-${dd}`;
  };
  const fromIso = toIso(dateFrom);
  const toIsoVal = toIso(dateTo);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(endpoint, {
        params: {
          search: globalSearch || undefined,
          date_from: fromIso || undefined,
          date_to: toIsoVal || undefined,
          limit: 2000,
        },
      });
      setRows(r.data?.rows || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [endpoint, globalSearch, fromIso, toIsoVal]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const filtered = useMemo(() => {
    return rows.filter((r) => Object.entries(colFilters).every(([k, v]) => {
      if (!v) return true;
      return String(r[k] || "").toLowerCase().includes(String(v).toLowerCase());
    }));
  }, [rows, colFilters]);

  const doExport = async () => {
    setExporting(true);
    try {
      const r = await api.get(exportPath, {
        params: {
          search: globalSearch || undefined,
          date_from: fromIso || undefined,
          date_to: toIsoVal || undefined,
        },
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.download = `${endpoint.split("/").pop()}_${stamp}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setExporting(false); }
  };

  return (
    <Layout>
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="font-serif text-3xl font-bold text-[#1f2320]"
                data-testid="tracker-heading">{heading}</h1>
            <div className="text-sm text-[#6e7770]">{filtered.length} rows</div>
          </div>
          <button className="btn-vgws"
                  onClick={doExport} disabled={exporting}
                  data-testid="btn-export-xlsx">
            <Download size={14} /> {exporting ? "Exporting…" : "Export to Excel"}
          </button>
        </div>

        <div className="bg-white border border-[#e2d7c3] rounded-lg p-4 mb-4 grid sm:grid-cols-4 gap-3">
          <label className="text-xs uppercase tracking-widest text-[#2d6b6b] font-semibold sm:col-span-2">
            <span className="flex items-center gap-1">
              <Search size={12} /> Global search
            </span>
            <input className="input-vgws mt-1 text-sm" placeholder="Ref code / applicant / any column"
                   value={globalSearch} onChange={(e) => setGlobalSearch(e.target.value)}
                   data-testid="tracker-global-search" />
          </label>
          <label className="text-xs uppercase tracking-widest text-[#2d6b6b] font-semibold">
            {dateRangeKey.replace(/_/g, " ")} from
            <input type="text" inputMode="numeric" maxLength={10}
                   className="input-vgws mt-1 text-sm placeholder:text-[#c7bda8] placeholder:italic"
                   value={dateFrom}
                   onChange={(e) => setDateFrom(_maskDdmmyyyy(e.target.value))}
                   placeholder="DD/MM/YYYY"
                   aria-label={`${dateRangeKey} from, DD/MM/YYYY`}
                   data-testid="tracker-date-from" />
            {!dateFrom && (
              <span className="block text-[10px] text-[#9a938a] italic mt-0.5 normal-case"
                    data-testid="tracker-date-from-hint">
                Format: DD/MM/YYYY
              </span>
            )}
          </label>
          <label className="text-xs uppercase tracking-widest text-[#2d6b6b] font-semibold">
            {dateRangeKey.replace(/_/g, " ")} to
            <input type="text" inputMode="numeric" maxLength={10}
                   className="input-vgws mt-1 text-sm placeholder:text-[#c7bda8] placeholder:italic"
                   value={dateTo}
                   onChange={(e) => setDateTo(_maskDdmmyyyy(e.target.value))}
                   placeholder="DD/MM/YYYY"
                   aria-label={`${dateRangeKey} to, DD/MM/YYYY`}
                   data-testid="tracker-date-to" />
            {!dateTo && (
              <span className="block text-[10px] text-[#9a938a] italic mt-0.5 normal-case"
                    data-testid="tracker-date-to-hint">
                Format: DD/MM/YYYY
              </span>
            )}
          </label>
        </div>

        <div className="bg-white border border-[#e2d7c3] rounded-lg overflow-auto">
          <table className="w-full text-sm" data-testid="tracker-table">
            <thead className="bg-[#7a1f2b] text-white sticky top-0">
              <tr>
                {columns.map((c) => (
                  <th key={c.key}
                      className="px-3 py-2 text-left font-semibold text-xs uppercase tracking-wider"
                      style={c.width ? { minWidth: c.width } : undefined}>
                    {c.label}
                  </th>
                ))}
              </tr>
              <tr className="bg-[#faf5eb] text-[#1f2320]">
                {columns.map((c) => (
                  <th key={`f-${c.key}`} className="px-2 py-1">
                    {c.type === "select" ? (
                      <select className="input-vgws text-xs !py-1"
                              value={colFilters[c.key] || ""}
                              onChange={(e) => setColFilters({ ...colFilters, [c.key]: e.target.value })}
                              data-testid={`filter-${c.key}`}>
                        <option value="">All</option>
                        {(c.options || []).map((o) => (
                          <option key={o} value={o}>{o}</option>
                        ))}
                      </select>
                    ) : (
                      <input className="input-vgws text-xs !py-1"
                             placeholder="filter…"
                             value={colFilters[c.key] || ""}
                             onChange={(e) => setColFilters({ ...colFilters, [c.key]: e.target.value })}
                             data-testid={`filter-${c.key}`} />
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={columns.length} className="text-center py-6 text-[#6e7770]">Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={columns.length} className="text-center py-6 text-[#6e7770]">No rows.</td></tr>
              )}
              {!loading && filtered.map((r, idx) => (
                <tr key={r.ref_code || idx} className="odd:bg-white even:bg-[#faf5eb]">
                  {columns.map((c) => (
                    <td key={c.key} className="px-3 py-2 border-b border-[#e2d7c3]/60 align-top">
                      {r[c.key] || "—"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
